import { AppModal } from "@/components/modals/AppModal/AppModal";
import { toast } from "react-toastify";
import { useEffect, useState } from "react";
import { Button, Textarea, Box, Typography } from "@mui/joy";
import { FormattedMessage } from "react-intl";
import { useAppContext } from "@/modules/app/providers/AppContextProvider";
import { CreateReviewInput } from "@/__generated__/graphql";
import { gql } from "@apollo/client";
import { useMutation } from "@apollo/client";
import { Rating } from "@mui/material";
import {
  canAskForYandexReview,
  googleUrlForBranch,
  markYandexReviewAsked,
  markYandexReviewDone,
  trackAsk,
  yandexUrlForBranch,
} from "./yandexReview";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useIntl } from "react-intl";

const calculateDaysDifference = (currentDate: Date, lastDate: Date) => {
  if (!currentDate || !lastDate) return null;
  const timeDifference = currentDate.getTime() - lastDate.getTime();
  return timeDifference / (1000 * 60 * 60 * 24);
};

enum Source {
  App = "APP",
}

const CREATE_REVIEW = gql`
  mutation CreateReview($input: CreateReviewInput!) {
    createReview(input: $input) {
      recId
      name
      review
    }
  }
`;

export default function ReviewRating() {
  const intl = useIntl();
  const [createReview] = useMutation(CREATE_REVIEW);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { user, allPasses } = useAppContext();
  const [open, setOpen] = useState(false);
  const [ratingValue, setRatingValue] = useState<number>(0);
  /** Второй шаг: показываем его только тем, кто поставил пятёрку. */
  const [askYandex, setAskYandex] = useState(false);
  /** Текст пишут единицы: из 468 оценок в приложении со словами всего 11.
      Поэтому благодарим по-разному — за отзыв и просто за оценку. */
  const [leftText, setLeftText] = useState(false);

  useEffect(() => {
    if (user) {
      const daysSinceLastVisit: null | number =
        user.dateLastVisit && calculateDaysDifference(new Date(), new Date(user.dateLastVisit));
      const daysSinceLastReview: undefined | number =
        localStorage.dateLastOpenRating &&
        calculateDaysDifference(new Date(), new Date(localStorage.dateLastOpenRating));

      const shouldAskForReview =
        daysSinceLastVisit !== null &&
        daysSinceLastVisit <= 7 &&
        (daysSinceLastReview === undefined || daysSinceLastReview > 30);

      if (shouldAskForReview) {
        setOpen(true);
        localStorage.dateLastOpenRating = new Date().toISOString();
      }
    }
  }, [user]);

  const onClose = () => {
    setOpen(false);
    setAskYandex(false);
  };

  // Ведём человека в отзывы того филиала, куда он ходит: последний абонемент —
  // самый точный ориентир, который у нас есть в приложении.
  const branchRecId = allPasses?.[allPasses.length - 1]?.branchRecId;

  const openReviews = (url: string, platform: "yandex" | "google") => {
    trackAsk("clicked", { platform, branchRecId, hadText: leftText });
    markYandexReviewDone();
    window.open(url, "_blank", "noopener");
    onClose();
  };

  const onAlreadyLeft = () => {
    trackAsk("already", { branchRecId, hadText: leftText });
    markYandexReviewDone();
    onClose();
  };

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formElements = e.target as HTMLFormElement;
    const feedback = formElements.elements.namedItem("feedback") as HTMLTextAreaElement;

    const input: CreateReviewInput = {
      dateReview: new Date().toISOString().split("T")[0],
      score: ratingValue * 2,
      branch: "Онлайн",
      name: `${user?.name} ${user?.surname}` || "",
      phone: user?.phone || "",
      review: feedback.value,
      source: Source.App,
    };

    try {
      await createReview({ variables: { input } });
      // Высшая оценка — единственный момент, когда просьба про отзыв уместна.
      setLeftText(Boolean(feedback.value.trim()));
      if (ratingValue === 5 && canAskForYandexReview()) {
        markYandexReviewAsked();
        trackAsk("shown", { branchRecId, hadText: Boolean(feedback.value.trim()) });
        setAskYandex(true);
      } else {
        setOpen(false);
      }
    } catch {
      toast.error(
        intl.formatMessage({ defaultMessage: "Не удалось отправить отзыв, пожалуйста, сообщите нам об этом" })
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const theme = createTheme();

  if (askYandex) {
    return (
      <AppModal open={open} onClose={onClose}>
        <Box sx={{ display: "grid", gap: "14px" }}>
          <Typography sx={{ fontWeight: "bold" }}>
            <FormattedMessage defaultMessage="Спасибо! 💜" />
          </Typography>
          <Typography>
            {leftText ? (
              <FormattedMessage defaultMessage="Так приятно 🥹 Ваши слова очень помогают нам — и ещё сильнее тем, кто только выбирает студию и боится сделать первый шаг 💜 Расскажете о нас на картах? Это пара минут ✨" />
            ) : (
              <FormattedMessage defaultMessage="Так приятно 🥹 Ваши пять звёзд для нас очень много значат 💜 А если найдётся минутка написать пару слов на картах — это поможет тем, кто только выбирает студию и боится сделать первый шаг ✨" />
            )}
          </Typography>
          <Button sx={{ display: "block", width: "100%" }} onClick={() => openReviews(yandexUrlForBranch(branchRecId), "yandex")}>
            <FormattedMessage defaultMessage="Написать на Яндекс.Картах 🗺️" />
          </Button>
          <Button
            variant="soft"
            sx={{ display: "block", width: "100%" }}
            onClick={() => openReviews(googleUrlForBranch(branchRecId), "google")}
          >
            <FormattedMessage defaultMessage="Или на Google Картах 🌍" />
          </Button>
          <Button variant="plain" sx={{ display: "block", width: "100%" }} onClick={onAlreadyLeft}>
            <FormattedMessage defaultMessage="Уже оставляла 💕" />
          </Button>
        </Box>
      </AppModal>
    );
  }

  return (
    <AppModal open={open} onClose={onClose}>
      <form onSubmit={onSubmit}>
        <Typography sx={{ fontWeight: "bold" }}>
          <FormattedMessage defaultMessage="Как проходят ваши занятия?" />
        </Typography>

        <Box sx={{ textAlign: "center" }}>
          <ThemeProvider theme={theme}>
            <Rating
              name="rating"
              value={ratingValue}
              precision={1}
              sx={{ fontSize: "2.5rem", color: "pink", padding: "20px 0" }}
              onChange={(_, newValue) => setRatingValue(Number(newValue))}
            />
          </ThemeProvider>
        </Box>

        <Textarea
          name="feedback"
          placeholder={intl.formatMessage({
            defaultMessage: "Напишите Ваш отзыв ✍️ Мы читаем каждый и стараемся сделать нашу школу лучше для Вас 💜",
          })}
          minRows={3}
          maxRows={5}
          sx={{
            marginBottom: "10px",
          }}
        />

        <Button
          sx={{ width: "calc(100% - 30px)", display: "block" }}
          type="submit"
          disabled={ratingValue < 1 || isSubmitting}
        >
          <FormattedMessage defaultMessage="Отправить" />
        </Button>
      </form>
    </AppModal>
  );
}
